import React, { useState, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { X, Calendar, CalendarRange, School, Building2, Users, MapPin, Clock, FileText, Link as LinkIcon, Briefcase, AlertTriangle, Pencil, Trash2 } from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'
import { parseISO, eachDayOfInterval, isWeekend, format, addDays, addMonths } from 'date-fns'
import { calendarKeys } from '@/hooks/useCalendar'
import { useEventTypes } from '@/hooks/useEventTypes'
import { getTodayDateString, extractDateInTimezoneSync } from '@/utils/timezoneUtils'

interface District {
  id: string
  district_name: string
}

interface School {
  id: string
  name: string
  district_id: string
}

interface User {
  id: string
  display_name: string
  username: string
}

type EventScope = 'family' | 'school' | 'district' | 'override'

interface UnifiedAddEventModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  initialDate?: string
  initialScope?: EventScope
  isAdmin?: boolean
  isKid?: boolean
  currentUserId?: string
  duplicateEvent?: any // Event to duplicate
  editEvent?: any // Event to edit
}

// Event types are fetched from the backend via useEventTypes hook

export function UnifiedAddEventModal({
  isOpen,
  onClose,
  onSuccess,
  initialDate,
  initialScope = 'family',
  isAdmin = false,
  isKid = false,
  currentUserId,
  duplicateEvent,
  editEvent
}: UnifiedAddEventModalProps) {
  const isEditing = !!editEvent
  const queryClient = useQueryClient()
  const eventTypes = useEventTypes()
  const [scope, setScope] = useState<EventScope>(initialScope)
  const [districts, setDistricts] = useState<District[]>([])
  const [schools, setSchools] = useState<School[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [overlappingEvents, setOverlappingEvents] = useState<any[]>([])
  const [savedLocations, setSavedLocations] = useState<{ id: string; name: string; type: string; address?: string }[]>([])
  const [editingLocationId, setEditingLocationId] = useState<string | null>(null)
  const [editLocationName, setEditLocationName] = useState('')
  const [editLocationAddress, setEditLocationAddress] = useState('')
  const [managingLocations, setManagingLocations] = useState(false)
  // Track the ID of the event being edited to prevent re-initialization on re-renders
  const [initializedEditEventId, setInitializedEditEventId] = useState<string | null>(null)

  // Backend handles custody feature gating; additionally filter for kids
  const availableEventTypes = {
    family: isKid ? eventTypes.family.filter(type => type.value !== 'custody') : eventTypes.family,
    school: eventTypes.school,
    district: eventTypes.district,
    override: isKid ? eventTypes.override.filter(type => type.value !== 'at_parent1' && type.value !== 'at_parent2') : eventTypes.override,
  }

  // Filter users for attendee selection - kids can only select themselves
  const availableAttendees = isKid
    ? users.filter(u => u.id === currentUserId)
    : users

  // Get timezone-aware today (sync with fallback to America/New_York)
  const getDefaultDate = () => initialDate || extractDateInTimezoneSync(new Date().toISOString())

  // Parse a combined location string ("Name | Address") into parts
  const parseLocation = (loc: string): { name: string; address: string } => {
    if (!loc) return { name: '', address: '' }
    const parts = loc.split(' | ')
    if (parts.length >= 2) return { name: parts[0], address: parts.slice(1).join(' | ') }
    // Legacy: check if it looks like an address (has digits or commas)
    if (/\d/.test(loc) || loc.includes(',')) return { name: '', address: loc }
    return { name: loc, address: '' }
  }

  // Combine name + address into a single location string
  const combineLocation = (name: string, address: string): string => {
    const n = name.trim()
    const a = address.trim()
    if (n && a) return `${n} | ${a}`
    return n || a
  }

  const [formData, setFormData] = useState({
    event_name: '',
    event_type: '',  // Will be set in useEffect based on available types
    description: '',
    event_date: getDefaultDate(),
    end_date: '',
    use_date_range: false,
    skip_weekends: false,
    start_time: '',
    end_time: '',
    all_day: true,
    location_name: '',
    location: '',
    url: '',
    district_id: '',
    school_id: '',
    affects_transportation: false,
    no_school: false,
    early_release: false,
    remote_learning: false,
    affects_chores: false,
    needs_ride: false,
    attendees: [] as string[],
    // Unified event detail fields
    opponent: '',
    event_subtype: '',
    game_type: '' as '' | 'home' | 'away',
    jersey_color: '',
    arrival_minutes: 30,
    is_cancelled: false,
    // Recurring event fields
    is_recurring: false,
    recurrence_pattern: 'weekly' as 'daily' | 'weekly' | 'monthly',
    recurrence_interval: 1,
    recurrence_days: [] as string[],
    recurrence_end_type: 'date' as 'date' | 'occurrences',
    recurrence_end_date: '',
    recurrence_occurrences: 10
  })

  useEffect(() => {
    if (isOpen) {
      // Reset form when opening for a new event (not edit, not duplicate)
      if (!isEditing && !duplicateEvent) {
        setInitializedEditEventId(null)
        setFormData(prev => ({
          ...prev,
          event_name: '',
          description: '',
          event_date: initialDate || getDefaultDate(),
          end_date: '',
          use_date_range: false,
          skip_weekends: false,
          start_time: '',
          end_time: '',
          all_day: true,
          location_name: '',
          location: '',
          url: '',
          affects_chores: false,
          needs_ride: false,
          attendees: [],
          opponent: '',
          event_subtype: '',
          game_type: '',
          jersey_color: '',
          arrival_minutes: 30,
          is_recurring: false,
          recurrence_pattern: 'weekly',
          recurrence_interval: 1,
          recurrence_days: [],
          recurrence_end_type: 'date',
          recurrence_end_date: '',
          recurrence_occurrences: 10
        }))
        setOverlappingEvents([])
      }
      loadDistricts()
      loadSchools()
      loadSavedLocations()
      if (scope === 'family' || scope === 'override') {
        loadUsers()
      }
      // Initialize event_type on open - but NOT when editing (edit mode sets its own values)
      if (!isEditing && !formData.event_type && availableEventTypes[scope].length > 0) {
        setFormData(prev => ({
          ...prev,
          event_type: availableEventTypes[scope][0].value
        }))
      }
    }
  }, [isOpen, scope, eventTypes, isEditing])

  useEffect(() => {
    // Reset form when scope changes - but NOT when editing (edit mode sets its own values)
    if (!isEditing) {
      setFormData(prev => ({
        ...prev,
        event_type: availableEventTypes[scope][0]?.value || '',
        district_id: '',
        school_id: ''
      }))
    }
  }, [scope, eventTypes, isEditing])

  // Update event_date when initialDate changes and modal opens (for new events)
  useEffect(() => {
    if (isOpen && initialDate && !isEditing && !duplicateEvent) {
      setFormData(prev => ({
        ...prev,
        event_date: initialDate
      }))
    }
  }, [isOpen, initialDate, isEditing, duplicateEvent])

  // Populate form when duplicating an event
  useEffect(() => {
    if (duplicateEvent && isOpen && !editEvent) {
      setFormData({
        event_name: duplicateEvent.title || '',
        event_type: duplicateEvent.event_type || '',
        description: duplicateEvent.description || '',
        event_date: getDefaultDate(),
        end_date: '',
        use_date_range: false,
        skip_weekends: false,
        start_time: duplicateEvent.start_time || '',
        end_time: duplicateEvent.end_time || '',
        all_day: duplicateEvent.all_day ?? true,
        location_name: parseLocation(duplicateEvent.location || '').name,
        location: parseLocation(duplicateEvent.location || '').address || duplicateEvent.location || '',
        url: duplicateEvent.url || '',
        district_id: duplicateEvent.district_id || '',
        school_id: duplicateEvent.school_id || '',
        affects_transportation: duplicateEvent.affects_transportation || false,
        no_school: duplicateEvent.no_school || false,
        early_release: duplicateEvent.early_release || false,
        remote_learning: duplicateEvent.remote_learning || false,
        is_cancelled: duplicateEvent.is_cancelled || false,
        affects_chores: duplicateEvent.affects_chores || false,
        needs_ride: duplicateEvent.needs_ride || false,
        attendees: duplicateEvent.attendees || [],
        opponent: duplicateEvent.opponent || '',
        event_subtype: duplicateEvent.event_subtype || '',
        game_type: (duplicateEvent.game_type as '' | 'home' | 'away') || '',
        jersey_color: duplicateEvent.jersey_color || '',
        arrival_minutes: duplicateEvent.arrival_minutes || 30,
        is_recurring: false,
        recurrence_pattern: 'weekly',
        recurrence_interval: 1,
        recurrence_days: [],
        recurrence_end_type: 'date',
        recurrence_end_date: '',
        recurrence_occurrences: 10
      })
    }
  }, [duplicateEvent, isOpen, initialDate, editEvent])

  // Populate form when editing an event - only initialize once per event
  useEffect(() => {
    if (editEvent && isOpen && editEvent.id !== initializedEditEventId) {
      // Mark this event as initialized to prevent re-initialization on re-renders
      setInitializedEditEventId(editEvent.id)

      // Set the correct scope based on event type
      if (editEvent.type === 'availability') {
        setScope('override')
      } else if (editEvent.type === 'school') {
        setScope('school')
      } else if (editEvent.type === 'district') {
        setScope('district')
      } else {
        setScope('family')
      }

      // Extract the date part from the event date (handle ISO format with timezone awareness)
      const eventDate = editEvent.date ? extractDateInTimezoneSync(editEvent.date) : getDefaultDate()
      setFormData({
        event_name: editEvent.title || '',
        event_type: editEvent.event_type || '',
        description: editEvent.description || '',
        event_date: eventDate,
        end_date: '',
        use_date_range: false,
        skip_weekends: false,
        start_time: editEvent.start_time ? editEvent.start_time.substring(0, 5) : '',
        end_time: editEvent.end_time ? editEvent.end_time.substring(0, 5) : '',
        all_day: editEvent.all_day ?? true,
        location_name: parseLocation(editEvent.location || '').name,
        location: parseLocation(editEvent.location || '').address || editEvent.location || '',
        url: editEvent.url || '',
        district_id: editEvent.district_id || '',
        school_id: editEvent.school_id || '',
        affects_transportation: editEvent.affects_transportation || false,
        no_school: editEvent.no_school || false,
        early_release: editEvent.early_release || false,
        remote_learning: editEvent.remote_learning || false,
        is_cancelled: editEvent.is_cancelled || false,
        affects_chores: editEvent.affects_chores || false,
        needs_ride: editEvent.needs_ride || false,
        attendees: editEvent.user_id ? [editEvent.user_id] : (editEvent.attendees || []),
        opponent: editEvent.opponent || '',
        // If game_type is set but event_subtype isn't, default to "Game"
        event_subtype: editEvent.event_subtype || (editEvent.game_type ? 'Game' : ''),
        game_type: (editEvent.game_type as '' | 'home' | 'away') || '',
        jersey_color: editEvent.jersey_color || '',
        arrival_minutes: editEvent.arrival_minutes ?? 30,
        is_recurring: false,
        recurrence_pattern: 'weekly',
        recurrence_interval: 1,
        recurrence_days: [],
        recurrence_end_type: 'date',
        recurrence_end_date: '',
        recurrence_occurrences: 10
      })
    }
  }, [editEvent, isOpen, initializedEditEventId])

  // Check for overlapping events when relevant fields change
  useEffect(() => {
    if (isOpen) {
      checkForOverlaps()
    }
  }, [formData.event_date, formData.start_time, formData.end_time, formData.all_day, formData.attendees, scope, isOpen])

  // Default end time to 1 hour after start time (for new events only)
  useEffect(() => {
    if (formData.start_time && !editEvent && !formData.end_time) {
      // Parse the start time and add 1 hour
      const [hours, minutes] = formData.start_time.split(':').map(Number)
      const endHours = (hours + 1) % 24
      const endTime = `${endHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
      setFormData(prev => ({ ...prev, end_time: endTime }))
    }
  }, [formData.start_time, editEvent])

  const loadDistricts = async () => {
    try {
      const data = await apiClient.get('/api/admin/district-calendars')
      setDistricts(data || [])
      if (data && data.length > 0 && scope === 'district') {
        setFormData(prev => ({ ...prev, district_id: data[0].id }))
      }
    } catch (error) {
      console.error('Failed to load districts:', error)
    }
  }

  const loadSchools = async () => {
    try {
      const data = await apiClient.get('/api/schools')
      setSchools(data || [])
      if (data && data.length > 0 && scope === 'school') {
        setFormData(prev => ({ ...prev, school_id: data[0].id }))
      }
    } catch (error) {
      console.error('Failed to load schools:', error)
    }
  }

  const loadUsers = async () => {
    try {
      const data = await apiClient.get('/api/users')
      // Handle both array response and object with users array
      const usersList = Array.isArray(data) ? data : (data.users || [])
      setUsers(usersList)
    } catch (error) {
      console.error('Failed to load users:', error)
    }
  }

  const loadSavedLocations = async () => {
    try {
      const locations = await apiClient.getSavedLocations()
      setSavedLocations(locations)
    } catch (error) {
      console.error('Failed to load saved locations:', error)
    }
  }


  const handleDeleteLocation = async (id: string, name: string) => {
    if (!confirm(`Delete "${name}" from saved locations?`)) return
    try {
      await apiClient.deleteLocation(id)
      toast.success(`Deleted "${name}"`)
      loadSavedLocations()
    } catch {
      toast.error('Failed to delete location')
    }
  }

  const handleUpdateLocation = async (id: string) => {
    if (!editLocationName.trim()) {
      toast.error('Location name is required')
      return
    }
    try {
      const addressParts = editLocationAddress.split(',').map(p => p.trim())
      const locationData: any = { name: editLocationName.trim(), type: 'other' }
      if (addressParts.length >= 3) {
        locationData.street_address = addressParts[0]
        locationData.city = addressParts[1]
        const stateZip = addressParts[2].split(' ')
        locationData.state = stateZip[0]
        if (stateZip.length > 1) locationData.zip_code = stateZip.slice(1).join(' ')
      } else {
        locationData.street_address = editLocationAddress
      }
      await apiClient.updateLocation(id, locationData)
      toast.success(`Updated "${editLocationName}"`)
      setEditingLocationId(null)
      loadSavedLocations()
    } catch (error: any) {
      if (error?.response?.status === 409 || error?.message?.includes('duplicate')) {
        toast.error('A location with this name already exists')
      } else {
        toast.error('Failed to update location')
      }
    }
  }

  const checkForOverlaps = async () => {
    // Only check for family and override events with attendees
    if (scope !== 'family' && scope !== 'override') {
      setOverlappingEvents([])
      return
    }

    if (!formData.event_date || formData.attendees.length === 0) {
      setOverlappingEvents([])
      return
    }

    try {
      const monthStart = format(parseISO(formData.event_date), 'yyyy-MM-01')
      const monthEnd = format(parseISO(formData.event_date), 'yyyy-MM-dd')

      const response = await apiClient.get(`/api/calendar/unified?start=${monthStart}&end=${monthEnd}`)
      // Handle both array response and object with events array
      const events = Array.isArray(response) ? response : (response.events || [])

      // Filter events for the same date
      const sameDateEvents = events.filter((evt: any) => evt.date === formData.event_date)

      // Check for overlaps
      const overlaps = sameDateEvents.filter((evt: any) => {
        // Skip the event being edited (don't flag self-overlap)
        if (isEditing && editEvent && evt.id === editEvent.id) return false

        // Skip if no attendees to compare
        if (!evt.attendees || evt.attendees.length === 0) return false

        // Check if any attendees match
        const hasMatchingAttendees = formData.attendees.some(attendeeId =>
          evt.attendees.includes(attendeeId)
        )

        if (!hasMatchingAttendees) return false

        // If all-day events, they overlap
        if (formData.all_day || evt.all_day) return true

        // Check time overlap
        if (!formData.start_time || !formData.end_time || !evt.start_time || !evt.end_time) {
          return true // If times are missing, assume overlap
        }

        const newStart = formData.start_time
        const newEnd = formData.end_time
        const existingStart = evt.start_time
        const existingEnd = evt.end_time

        // Check if times overlap
        return (newStart < existingEnd && newEnd > existingStart)
      })

      setOverlappingEvents(overlaps)
    } catch (error) {
      console.error('Failed to check for overlaps:', error)
      setOverlappingEvents([])
    }
  }

  const generateRecurringDates = (): string[] => {
    if (!formData.is_recurring) {
      return [formData.event_date]
    }

    const dates: string[] = []
    const startDate = parseISO(formData.event_date)
    const { recurrence_pattern, recurrence_interval, recurrence_days, recurrence_end_type, recurrence_end_date, recurrence_occurrences } = formData

    let currentDate = startDate
    let count = 0
    const maxDate = recurrence_end_type === 'date' && recurrence_end_date
      ? parseISO(recurrence_end_date)
      : addDays(startDate, 365) // Max 1 year

    while (count < (recurrence_end_type === 'occurrences' ? recurrence_occurrences : 365)) {
      // Check if we've exceeded the end date
      if (recurrence_end_type === 'date' && currentDate > maxDate) {
        break
      }

      // Add current date based on pattern
      if (recurrence_pattern === 'daily') {
        dates.push(format(currentDate, 'yyyy-MM-dd'))
        currentDate = addDays(currentDate, recurrence_interval)
      } else if (recurrence_pattern === 'weekly') {
        // For weekly, check if current day is in selected days
        const dayOfWeek = format(currentDate, 'EEEE').toLowerCase()
        if (recurrence_days.length === 0 || recurrence_days.includes(dayOfWeek)) {
          dates.push(format(currentDate, 'yyyy-MM-dd'))
        }
        // Move to next day, or next week if we've checked all 7 days
        const nextDay = addDays(currentDate, 1)
        if (recurrence_days.length > 0 && format(nextDay, 'EEEE').toLowerCase() === recurrence_days[0]) {
          // Completed one week cycle, jump to next interval
          currentDate = addDays(currentDate, 7 * recurrence_interval - 6)
        } else {
          currentDate = nextDay
        }
      } else if (recurrence_pattern === 'monthly') {
        dates.push(format(currentDate, 'yyyy-MM-dd'))
        currentDate = addMonths(currentDate, recurrence_interval)
      }

      count++
      if (count > 1000) break // Safety limit
    }

    return dates
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      let endpoint = ''
      let payload: any = {
        event_name: formData.event_name,
        event_type: formData.event_type,
        description: formData.description || undefined,
        event_date: formData.event_date,
        start_time: formData.start_time || undefined,
        end_time: formData.end_time || undefined,
        url: formData.url || undefined
      }

      if (scope === 'family') {
        endpoint = '/api/events'
        payload = {
          title: formData.event_name,
          event_type: formData.event_type,
          description: formData.description,
          event_date: formData.event_date,
          start_time: formData.start_time,
          end_time: formData.end_time,
          all_day: formData.all_day,
          location: combineLocation(formData.location_name, formData.location),
          affects_chores: formData.affects_chores,
          needs_ride: formData.needs_ride,
          invite_user_ids: formData.attendees
        }

        // Add unified event detail fields
        if (formData.opponent) {
          payload.opponent = formData.opponent
        }
        if (formData.event_subtype) {
          payload.event_subtype = formData.event_subtype
        }

        // Handle date range for family events
        if (formData.use_date_range && formData.end_date) {
          const startDate = parseISO(formData.event_date)
          const endDate = parseISO(formData.end_date)
          const allDates = eachDayOfInterval({ start: startDate, end: endDate })

          const datesToCreate = allDates
            .filter(date => !formData.skip_weekends || !isWeekend(date))
            .map(date => format(date, 'yyyy-MM-dd'))

          // Create event for each date in range
          for (const date of datesToCreate) {
            await apiClient.post(endpoint, { ...payload, event_date: date })
          }

          toast.success(`Created ${datesToCreate.length} event${datesToCreate.length > 1 ? 's' : ''} successfully`)
          // Invalidate and refetch calendar cache to ensure fresh data
          await queryClient.invalidateQueries({
            queryKey: calendarKeys.events(),
            refetchType: 'all'
          })
          await queryClient.refetchQueries({
            queryKey: calendarKeys.events(),
            type: 'active'
          })
          try {
            onSuccess()
          } catch (e) {
            console.error('Error in onSuccess callback:', e)
          }
          try {
            onClose()
          } catch (e) {
            console.error('Error in onClose callback:', e)
          }
          resetForm()
          setLoading(false)
          return
        }
      } else if (scope === 'school') {
        if (!isEditing && !formData.school_id) {
          toast.error('Please select a school')
          setLoading(false)
          return
        }
        endpoint = `/api/admin/schools/${formData.school_id}/calendar`
        payload.affects_transportation = formData.affects_transportation
        payload.no_school = formData.no_school
        payload.early_release = formData.early_release
        payload.remote_learning = formData.remote_learning
        payload.is_cancelled = formData.is_cancelled
        payload.location = combineLocation(formData.location_name, formData.location) || undefined

        // Add sports event fields for school events
        if (formData.opponent) {
          payload.opponent = formData.opponent
        }
        if (formData.event_subtype) {
          payload.event_subtype = formData.event_subtype
        }
        if (formData.game_type) {
          payload.game_type = formData.game_type
        }
        if (formData.jersey_color) {
          payload.jersey_color = formData.jersey_color
        }
        if (formData.arrival_minutes && formData.arrival_minutes !== 30) {
          payload.arrival_minutes = formData.arrival_minutes
        }
      } else if (scope === 'district') {
        endpoint = '/api/admin/school-events'
        payload.district_id = formData.district_id
        payload.affects_transportation = formData.affects_transportation
        payload.no_school = formData.no_school
        payload.early_release = formData.early_release
        payload.remote_learning = formData.remote_learning
        payload.is_cancelled = formData.is_cancelled
      } else if (scope === 'override') {
        // Create availability override for each selected child
        if (formData.attendees.length === 0) {
          toast.error('Please select at least one child')
          setLoading(false)
          return
        }

        // Generate list of dates to create overrides for
        let datesToCreate: string[] = []
        if (formData.use_date_range && formData.end_date) {
          const startDate = parseISO(formData.event_date)
          const endDate = parseISO(formData.end_date)
          const allDates = eachDayOfInterval({ start: startDate, end: endDate })

          datesToCreate = allDates
            .filter(date => !formData.skip_weekends || !isWeekend(date))
            .map(date => format(date, 'yyyy-MM-dd'))
        } else {
          datesToCreate = [formData.event_date]
        }

        // Create override for each child on each date
        for (const userId of formData.attendees) {
          for (const date of datesToCreate) {
            await apiClient.post('/api/availability/overrides', {
              user_id: userId,
              override_date: date,
              override_type: formData.event_type,
              reason: formData.description || undefined,
              location: combineLocation(formData.location_name, formData.location) || undefined,
              all_day: true
            })
          }
        }

        const overrideCount = formData.attendees.length * datesToCreate.length
        toast.success(`Created ${overrideCount} availability override${overrideCount > 1 ? 's' : ''} successfully`)
        // Invalidate and refetch calendar cache to ensure fresh data
        await queryClient.invalidateQueries({
          queryKey: calendarKeys.events(),
          refetchType: 'all'
        })
        await queryClient.refetchQueries({
          queryKey: calendarKeys.events(),
          type: 'active'
        })
        try {
          onSuccess()
        } catch (e) {
          console.error('Error in onSuccess callback:', e)
        }
        try {
          onClose()
        } catch (e) {
          console.error('Error in onClose callback:', e)
        }
        resetForm()
        setLoading(false)
        return
      }

      // Handle edit vs create
      if (isEditing && editEvent?.id) {
        if (editEvent.type === 'family') {
          // Edit existing family event
          await apiClient.put(`/api/events/${editEvent.id}`, payload)
          toast.success('Event updated successfully')
        } else if (editEvent.type === 'school' || editEvent.type === 'district') {
          // Edit existing school/district event using PATCH
          const schoolEventPayload = {
            event_name: formData.event_name,
            event_type: formData.event_type,
            description: formData.description,
            event_date: formData.event_date,
            start_time: formData.start_time || null,
            end_time: formData.end_time || null,
            location: combineLocation(formData.location_name, formData.location),
            affects_transportation: formData.affects_transportation,
            no_school: formData.no_school,
            early_release: formData.early_release,
            remote_learning: formData.remote_learning,
            game_type: formData.game_type || null,
            opponent: formData.opponent || null,
            event_subtype: formData.event_subtype || null,
            jersey_color: formData.jersey_color || null,
            arrival_minutes: formData.arrival_minutes || 30,
            is_cancelled: formData.is_cancelled
          }
          await apiClient.patch(`/api/admin/school-events/${editEvent.id}`, schoolEventPayload)
          toast.success('Event updated successfully')
        } else if (editEvent.type === 'availability') {
          // Edit existing availability override
          const overridePayload = {
            override_date: formData.event_date,
            override_type: formData.event_type,
            reason: formData.description || undefined,
            location: combineLocation(formData.location_name, formData.location) || undefined,
            all_day: formData.all_day
          }
          await apiClient.patch(`/api/availability/overrides/${editEvent.id}`, overridePayload)
          toast.success('Availability override updated successfully')
        } else {
          // Unknown event type during edit - log and show error
          console.error('Unknown edit event type:', editEvent.type)
          toast.error('Unable to update this event type')
          setLoading(false)
          return
        }
      } else if (formData.is_recurring && (scope === 'family' || scope === 'school' || scope === 'district')) {
        // Handle recurring events (only for new events)
        const recurringDates = generateRecurringDates()

        for (const date of recurringDates) {
          const recurringPayload = { ...payload, event_date: date }
          await apiClient.post(endpoint, recurringPayload)
        }

        toast.success(`${recurringDates.length} recurring events created successfully`)
      } else {
        await apiClient.post(endpoint, payload)
        toast.success(`${scope.charAt(0).toUpperCase() + scope.slice(1)} event created successfully`)
      }

      // Invalidate and refetch calendar cache to ensure fresh data
      await queryClient.invalidateQueries({
        queryKey: calendarKeys.events(),
        refetchType: 'all' // Force refetch even if window doesn't have focus
      })
      // Also refetch any active calendar queries
      await queryClient.refetchQueries({
        queryKey: calendarKeys.events(),
        type: 'active'
      })

      try {
        onSuccess()
      } catch (e) {
        console.error('Error in onSuccess callback:', e)
      }

      try {
        onClose()
      } catch (e) {
        console.error('Error in onClose callback:', e)
      }

      resetForm()
    } catch (error: any) {
      console.error(`Failed to ${isEditing ? 'update' : 'create'} event:`, error)
      // Check for duplicate event error
      const errorMessage = error?.response?.data?.error || error?.response?.data?.detail || error?.message
      const isDuplicate = error?.response?.status === 409 || error?.response?.data?.code === 'DUPLICATE_EVENT'
      if (isDuplicate) {
        toast.error('This event already exists. Please check the calendar or use a different name.')
      } else {
        toast.error(errorMessage || `Failed to ${isEditing ? 'update' : 'create'} event`)
      }
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    // Reset the edit tracking so next edit will properly initialize
    setInitializedEditEventId(null)
    setFormData({
      event_name: '',
      event_type: eventTypes[scope][0].value,
      description: '',
      event_date: getDefaultDate(),
      start_time: '',
      end_time: '',
      all_day: true,
      location_name: '',
      location: '',
      url: '',
      district_id: '',
      school_id: '',
      affects_transportation: false,
      no_school: false,
      early_release: false,
      remote_learning: false,
      affects_chores: false,
      needs_ride: false,
      attendees: [],
      opponent: '',
      event_subtype: '',
      game_type: '',
      jersey_color: '',
      arrival_minutes: 30
    })
  }

  const handleAttendeeToggle = (userId: string) => {
    setFormData(prev => ({
      ...prev,
      attendees: prev.attendees.includes(userId)
        ? prev.attendees.filter(id => id !== userId)
        : [...prev.attendees, userId]
    }))
  }

  if (!isOpen) return null

  const scopeIcon = {
    family: <Users className="w-5 h-5" />,
    school: <School className="w-5 h-5" />,
    district: <Building2 className="w-5 h-5" />,
    override: <MapPin className="w-5 h-5" />
  }

  const scopeColor = {
    family: 'bg-blue-500',
    school: 'bg-green-500',
    district: 'bg-purple-500',
    override: 'bg-orange-500'
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className={`${scopeColor[scope]} text-white p-6 flex items-center justify-between`}>
          <div className="flex items-center space-x-3">
            {scopeIcon[scope]}
            <h2 className="text-2xl font-bold">
              {scope === 'override'
                ? `${isEditing ? 'Edit' : 'Add'} Availability Override`
                : `${isEditing ? 'Edit' : 'Add'} ${scope.charAt(0).toUpperCase() + scope.slice(1)} Event`}
            </h2>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Event Scope Selector - Hidden when editing */}
          {!isEditing && (
          <div className="flex space-x-2 bg-gray-100 p-1 rounded-lg">
            <button
              type="button"
              onClick={() => setScope('family')}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                scope === 'family'
                  ? 'bg-blue-500 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Users className="w-4 h-4 inline mr-2" />
              Family Event
            </button>
            {/* Kids can't create availability overrides */}
            {!isKid && (
              <button
                type="button"
                onClick={() => setScope('override')}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  scope === 'override'
                    ? 'bg-orange-500 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <MapPin className="w-4 h-4 inline mr-2" />
                Availability
              </button>
            )}
            {isAdmin && (
              <>
                <button
                  type="button"
                  onClick={() => setScope('school')}
                  className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    scope === 'school'
                      ? 'bg-green-500 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <School className="w-4 h-4 inline mr-2" />
                  School Event
                </button>
                <button
                  type="button"
                  onClick={() => setScope('district')}
                  className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    scope === 'district'
                      ? 'bg-purple-500 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Building2 className="w-4 h-4 inline mr-2" />
                  District Event
                </button>
              </>
            )}
          </div>
          )}

          {/* School/District Selector */}
          {scope === 'district' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Building2 className="w-4 h-4 inline mr-1" />
                District *
              </label>
              <select
                value={formData.district_id}
                onChange={e => setFormData({ ...formData, district_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              >
                <option value="">Select district...</option>
                {districts.map(district => (
                  <option key={district.id} value={district.id}>{district.district_name}</option>
                ))}
              </select>
            </div>
          )}

          {scope === 'school' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <School className="w-4 h-4 inline mr-1" />
                School *
              </label>
              <select
                value={formData.school_id}
                onChange={e => setFormData({ ...formData, school_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                required
              >
                <option value="">Select school...</option>
                {schools.map(school => (
                  <option key={school.id} value={school.id}>{school.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Override Form - Simpler form for availability overrides */}
          {scope === 'override' ? (
            <>
              {/* Child Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Children *
                </label>
                <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3">
                  {availableAttendees.map(user => (
                    <label key={user.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                      <input
                        type="checkbox"
                        checked={formData.attendees.includes(user.id)}
                        onChange={() => handleAttendeeToggle(user.id)}
                        className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                      />
                      <span className="text-sm">{user.display_name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Override Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type *
                </label>
                <select
                  value={formData.event_type}
                  onChange={e => setFormData({ ...formData, event_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  required
                >
                  {availableEventTypes[scope].map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Date */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Date *
                  </label>
                  {!isEditing && (
                    <label className="flex items-center space-x-2 text-sm text-gray-600">
                      <input
                        type="checkbox"
                        checked={formData.use_date_range}
                        onChange={e => setFormData({ ...formData, use_date_range: e.target.checked, is_recurring: false })}
                        className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                      />
                      <span>Date range</span>
                    </label>
                  )}
                </div>

                <div className={formData.use_date_range ? 'grid grid-cols-2 gap-3' : ''}>
                  <div>
                    {formData.use_date_range && (
                      <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                    )}
                    <input
                      type="date"
                      value={formData.event_date}
                      onChange={e => setFormData({ ...formData, event_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      required
                    />
                  </div>
                  {formData.use_date_range && (
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">End Date</label>
                      <input
                        type="date"
                        value={formData.end_date}
                        min={formData.event_date}
                        onChange={e => setFormData({ ...formData, end_date: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        required={formData.use_date_range}
                      />
                    </div>
                  )}
                </div>

                {formData.use_date_range && (
                  <label className="flex items-center space-x-2 mt-2 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={formData.skip_weekends}
                      onChange={e => setFormData({ ...formData, skip_weekends: e.target.checked })}
                      className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                    />
                    <span>Skip weekends</span>
                  </label>
                )}

                {/* Recurring Events - Hidden when editing */}
                {!isEditing && !formData.use_date_range && (scope === 'family' || scope === 'school' || scope === 'district') && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <label className="flex items-center space-x-2 text-sm text-gray-600">
                      <input
                        type="checkbox"
                        checked={formData.is_recurring}
                        onChange={e => setFormData({ ...formData, is_recurring: e.target.checked, use_date_range: false })}
                        className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                      />
                      <span>Recurring event</span>
                    </label>

                    {formData.is_recurring && (
                      <div className="mt-3 space-y-3 pl-6">
                        {/* Recurrence Pattern */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Repeat</label>
                            <select
                              value={formData.recurrence_pattern}
                              onChange={e => setFormData({ ...formData, recurrence_pattern: e.target.value as 'daily' | 'weekly' | 'monthly' })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                            >
                              <option value="daily">Daily</option>
                              <option value="weekly">Weekly</option>
                              <option value="monthly">Monthly</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Every</label>
                            <input
                              type="number"
                              min="1"
                              max="52"
                              value={formData.recurrence_interval}
                              onChange={e => setFormData({ ...formData, recurrence_interval: parseInt(e.target.value) || 1 })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                            />
                          </div>
                        </div>

                        {/* Days of Week (for Weekly pattern) */}
                        {formData.recurrence_pattern === 'weekly' && (
                          <div>
                            <label className="block text-xs text-gray-500 mb-2">Repeat on</label>
                            <div className="flex flex-wrap gap-2">
                              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                                <label key={day} className="flex items-center space-x-1">
                                  <input
                                    type="checkbox"
                                    checked={formData.recurrence_days.includes(day)}
                                    onChange={e => {
                                      if (e.target.checked) {
                                        setFormData({ ...formData, recurrence_days: [...formData.recurrence_days, day] })
                                      } else {
                                        setFormData({ ...formData, recurrence_days: formData.recurrence_days.filter(d => d !== day) })
                                      }
                                    }}
                                    className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                                  />
                                  <span className="text-xs">{day.substring(0, 3).toUpperCase()}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* End Type */}
                        <div>
                          <label className="block text-xs text-gray-500 mb-2">Ends</label>
                          <div className="space-y-2">
                            <label className="flex items-center space-x-2">
                              <input
                                type="radio"
                                checked={formData.recurrence_end_type === 'date'}
                                onChange={() => setFormData({ ...formData, recurrence_end_type: 'date' })}
                                className="text-orange-500 focus:ring-orange-500"
                              />
                              <span className="text-sm">On date</span>
                            </label>
                            {formData.recurrence_end_type === 'date' && (
                              <input
                                type="date"
                                value={formData.recurrence_end_date}
                                onChange={e => setFormData({ ...formData, recurrence_end_date: e.target.value })}
                                className="ml-6 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                              />
                            )}

                            <label className="flex items-center space-x-2">
                              <input
                                type="radio"
                                checked={formData.recurrence_end_type === 'occurrences'}
                                onChange={() => setFormData({ ...formData, recurrence_end_type: 'occurrences' })}
                                className="text-orange-500 focus:ring-orange-500"
                              />
                              <span className="text-sm">After</span>
                            </label>
                            {formData.recurrence_end_type === 'occurrences' && (
                              <div className="ml-6 flex items-center space-x-2">
                                <input
                                  type="number"
                                  min="1"
                                  max="365"
                                  value={formData.recurrence_occurrences}
                                  onChange={e => setFormData({ ...formData, recurrence_occurrences: parseInt(e.target.value) || 1 })}
                                  className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                                />
                                <span className="text-sm text-gray-600">occurrences</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Location (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Location (optional)
                  {savedLocations.length > 0 && (
                    <button type="button" onClick={() => setManagingLocations(!managingLocations)} className="ml-2 text-xs text-orange-600 hover:text-orange-800 hover:underline">
                      {managingLocations ? 'Done' : 'Manage'}
                    </button>
                  )}
                </label>

                {/* Saved locations list with manage mode */}
                {savedLocations.length > 0 && (
                  managingLocations ? (
                    <div className="mb-2 border border-orange-200 rounded-md divide-y divide-orange-100">
                      {savedLocations.map(loc => (
                        <div key={loc.id} className="px-3 py-2">
                          {editingLocationId === loc.id ? (
                            <div className="space-y-2">
                              <input type="text" value={editLocationName} onChange={e => setEditLocationName(e.target.value)} className="w-full px-2 py-1 border border-orange-300 rounded text-sm" placeholder="Place name" />
                              <div className="flex gap-2">
                                <input type="text" value={editLocationAddress} onChange={e => setEditLocationAddress(e.target.value)} className="flex-1 px-2 py-1 border border-orange-300 rounded text-sm" placeholder="Address" />
                                <button type="button" onClick={() => handleUpdateLocation(loc.id)} className="px-2 py-1 bg-orange-500 text-white rounded text-xs hover:bg-orange-600">Save</button>
                                <button type="button" onClick={() => setEditingLocationId(null)} className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300">Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between">
                              <span className="text-sm">{loc.name}{loc.address ? ` - ${loc.address}` : ''}</span>
                              <div className="flex gap-1 ml-2">
                                <button type="button" onClick={() => { setEditingLocationId(loc.id); setEditLocationName(loc.name); setEditLocationAddress(loc.address || '') }} className="p-1 text-gray-400 hover:text-orange-600" title="Edit">
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button type="button" onClick={() => handleDeleteLocation(loc.id, loc.name)} className="p-1 text-gray-400 hover:text-red-600" title="Delete">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mb-2">
                      <select
                        value=""
                        onChange={e => {
                          const selected = savedLocations.find(l => l.id === e.target.value)
                          if (selected) {
                            setFormData({ ...formData, location_name: selected.name || '', location: selected.address || '' })
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      >
                        <option value="">Select saved location...</option>
                        {savedLocations.map(loc => (
                          <option key={loc.id} value={loc.id}>
                            {loc.name}{loc.address ? ` - ${loc.address}` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  )
                )}

                <div className="space-y-2">
                  <input
                    type="text"
                    value={formData.location_name}
                    onChange={e => setFormData({ ...formData, location_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Place name (e.g., Main Field, School Gym)"
                  />
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formData.location}
                      onChange={e => setFormData({ ...formData, location: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Address..."
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const searchQuery = formData.location || formData.location_name || '';
                        if (searchQuery) {
                          window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(searchQuery)}`, '_blank', 'width=1000,height=800');
                        } else {
                          window.open('https://www.google.com/maps', '_blank', 'width=1000,height=800');
                        }
                      }}
                      className="px-3 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 whitespace-nowrap text-sm transition-colors"
                    >
                      <MapPin className="w-4 h-4 inline mr-1" />
                      Maps
                    </button>
                  </div>
                </div>

                {/* Save location inline */}
                {(formData.location_name || formData.location) && !savedLocations.some(l => l.name === formData.location_name && l.address === formData.location) && (
                  <button
                    type="button"
                    onClick={async () => {
                      if (!formData.location_name.trim()) {
                        toast.error('Enter a place name to save this location')
                        return
                      }
                      try {
                        const addressParts = formData.location.split(',').map(p => p.trim())
                        const locationData: any = { name: formData.location_name.trim(), type: 'other' }
                        if (addressParts.length >= 3) {
                          locationData.street_address = addressParts[0]
                          locationData.city = addressParts[1]
                          const stateZip = addressParts[2].split(' ')
                          locationData.state = stateZip[0]
                          if (stateZip.length > 1) locationData.zip_code = stateZip.slice(1).join(' ')
                        } else {
                          locationData.street_address = formData.location
                        }
                        await apiClient.saveLocation(locationData)
                        toast.success(`Saved "${formData.location_name}" for quick access`)
                        loadSavedLocations()
                      } catch (error: any) {
                        if (error?.message?.includes('duplicate')) {
                          toast.error('A location with this name already exists')
                        } else {
                          toast.error('Failed to save location')
                        }
                      }
                    }}
                    className="mt-1 text-sm text-orange-600 hover:text-orange-800 hover:underline"
                  >
                    Save this location for future events
                  </button>
                )}
              </div>

              {/* Reason (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FileText className="w-4 h-4 inline mr-1" />
                  Reason (optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  rows={3}
                  placeholder="Enter reason..."
                />
              </div>
            </>
          ) : (
            <>
              {/* Event Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Event Name *
            </label>
            <input
              type="text"
              value={formData.event_name}
              onChange={e => setFormData({ ...formData, event_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Spring Break, Soccer Game, Birthday Party"
              required
            />
          </div>

          {/* Event Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type *
            </label>
            <select
              value={formData.event_type}
              onChange={e => {
                const newType = e.target.value
                // Auto-enable affects_chores for custody events
                const affectsChores = newType === 'custody' ? true : formData.affects_chores
                setFormData({ ...formData, event_type: newType, affects_chores: affectsChores })
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              {availableEventTypes[scope].map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Date and Time */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                <Calendar className="w-4 h-4 inline mr-1" />
                Date *
              </label>
              <div className="flex items-center gap-4">
                <label className="flex items-center space-x-2 text-sm text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.all_day}
                    onChange={e => setFormData({ ...formData, all_day: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span>All-day</span>
                </label>
                <label className="flex items-center space-x-2 text-sm text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.use_date_range}
                    onChange={e => setFormData({ ...formData, use_date_range: e.target.checked, is_recurring: false })}
                    className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                  />
                  <CalendarRange className="w-4 h-4" />
                  <span>Date range</span>
                </label>
              </div>
            </div>

            {formData.use_date_range ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={formData.event_date}
                    onChange={e => setFormData({ ...formData, event_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">End Date</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    min={formData.event_date}
                    onChange={e => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required={formData.use_date_range}
                  />
                </div>
              </div>
            ) : formData.all_day ? (
              <input
                type="date"
                value={formData.event_date}
                onChange={e => setFormData({ ...formData, event_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            ) : (
              <div className="grid grid-cols-3 gap-4 items-end">
                <div>
                  <input
                    type="date"
                    value={formData.event_date}
                    onChange={e => setFormData({ ...formData, event_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    <Clock className="w-3 h-3 inline mr-0.5" />
                    Start
                  </label>
                  <input
                    type="time"
                    value={formData.start_time}
                    onChange={e => setFormData({ ...formData, start_time: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    <Clock className="w-3 h-3 inline mr-0.5" />
                    End
                  </label>
                  <input
                    type="time"
                    value={formData.end_time}
                    onChange={e => setFormData({ ...formData, end_time: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {formData.use_date_range && (
              <label className="flex items-center space-x-2 mt-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={formData.skip_weekends}
                  onChange={e => setFormData({ ...formData, skip_weekends: e.target.checked })}
                  className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                />
                <span>Skip weekends</span>
              </label>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="w-4 h-4 inline mr-1" />
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              placeholder="Add any additional details..."
            />
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MapPin className="w-4 h-4 inline mr-1" />
              Location
              {savedLocations.length > 0 && (
                <button type="button" onClick={() => setManagingLocations(!managingLocations)} className="ml-2 text-xs text-blue-600 hover:text-blue-800 hover:underline">
                  {managingLocations ? 'Done' : 'Manage'}
                </button>
              )}
            </label>

            {/* Saved locations list with manage mode */}
            {savedLocations.length > 0 && (
              managingLocations ? (
                <div className="mb-2 border border-blue-200 rounded-md divide-y divide-blue-100">
                  {savedLocations.map(loc => (
                    <div key={loc.id} className="px-3 py-2">
                      {editingLocationId === loc.id ? (
                        <div className="space-y-2">
                          <input type="text" value={editLocationName} onChange={e => setEditLocationName(e.target.value)} className="w-full px-2 py-1 border border-blue-300 rounded text-sm" placeholder="Place name" />
                          <div className="flex gap-2">
                            <input type="text" value={editLocationAddress} onChange={e => setEditLocationAddress(e.target.value)} className="flex-1 px-2 py-1 border border-blue-300 rounded text-sm" placeholder="Address" />
                            <button type="button" onClick={() => handleUpdateLocation(loc.id)} className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600">Save</button>
                            <button type="button" onClick={() => setEditingLocationId(null)} className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <span className="text-sm">{loc.name}{loc.address ? ` - ${loc.address}` : ''}</span>
                          <div className="flex gap-1 ml-2">
                            <button type="button" onClick={() => { setEditingLocationId(loc.id); setEditLocationName(loc.name); setEditLocationAddress(loc.address || '') }} className="p-1 text-gray-400 hover:text-blue-600" title="Edit">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button type="button" onClick={() => handleDeleteLocation(loc.id, loc.name)} className="p-1 text-gray-400 hover:text-red-600" title="Delete">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mb-2">
                  <select
                    value=""
                    onChange={e => {
                      const selected = savedLocations.find(l => l.id === e.target.value)
                      if (selected) {
                        setFormData({ ...formData, location_name: selected.name || '', location: selected.address || '' })
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    <option value="">Select saved location...</option>
                    {savedLocations.map(loc => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name}{loc.address ? ` - ${loc.address}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )
            )}

            <div className="space-y-2">
              <input
                type="text"
                value={formData.location_name}
                onChange={e => setFormData({ ...formData, location_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Place name (e.g., Community Center, Grandma's House)"
              />
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.location}
                  onChange={e => setFormData({ ...formData, location: e.target.value })}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Address (e.g., 123 Main St, City, State)"
                />
                <button
                  type="button"
                  onClick={() => {
                    const searchQuery = formData.location || formData.location_name || '';
                    if (searchQuery) {
                      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(searchQuery)}`, '_blank', 'width=1000,height=800');
                    } else {
                      window.open('https://www.google.com/maps', '_blank', 'width=1000,height=800');
                    }
                  }}
                  className="px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 whitespace-nowrap text-sm transition-colors"
                >
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Maps
                </button>
              </div>

              {/* Save location button */}
              {(formData.location_name || formData.location) && !savedLocations.some(l => l.name === formData.location_name && l.address === formData.location) && (
                <button
                  type="button"
                  onClick={async () => {
                    if (!formData.location_name.trim()) {
                      toast.error('Enter a place name to save this location')
                      return
                    }
                    try {
                      const addressParts = formData.location.split(',').map(p => p.trim())
                      const locationData: any = {
                        name: formData.location_name.trim(),
                        type: formData.event_type === 'work' ? 'work' : 'other'
                      }
                      if (addressParts.length >= 3) {
                        locationData.street_address = addressParts[0]
                        locationData.city = addressParts[1]
                        const stateZip = addressParts[2].split(' ')
                        locationData.state = stateZip[0]
                        if (stateZip.length > 1) locationData.zip_code = stateZip.slice(1).join(' ')
                      } else {
                        locationData.street_address = formData.location
                      }
                      await apiClient.saveLocation(locationData)
                      toast.success(`Saved "${formData.location_name}" for quick access`)
                      loadSavedLocations()
                    } catch (error: any) {
                      if (error?.message?.includes('duplicate')) {
                        toast.error('A location with this name already exists')
                      } else {
                        toast.error('Failed to save location')
                      }
                    }
                  }}
                  className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Save this location for future events
                </button>
              )}
            </div>
          </div>

          {/* Event Details - Sports & Other */}
          {(formData.event_type === 'sports' || formData.event_type === 'work') && (
            <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="text-sm font-semibold text-blue-900 mb-3">Event Details</h3>

              {/* Event Subtype */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {formData.event_type === 'sports' ? 'Event Type' : 'Shift Type'}
                </label>
                <select
                  value={formData.event_subtype}
                  onChange={e => setFormData({ ...formData, event_subtype: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select...</option>
                  {formData.event_type === 'sports' ? (
                    <>
                      <option value="Game">Game</option>
                      <option value="Practice">Practice</option>
                      <option value="Scrimmage">Scrimmage</option>
                      <option value="Tournament">Tournament</option>
                    </>
                  ) : (
                    <>
                      <option value="Regular Shift">Regular Shift</option>
                      <option value="Overtime">Overtime</option>
                      <option value="Training">Training</option>
                    </>
                  )}
                </select>
              </div>

              {/* Opponent (Sports only) */}
              {formData.event_type === 'sports' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Opponent (optional)
                  </label>
                  <input
                    type="text"
                    value={formData.opponent}
                    onChange={e => setFormData({ ...formData, opponent: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Thunder FC, Riverside High"
                  />
                </div>
              )}

              {/* Home/Away (Sports games only) */}
              {formData.event_type === 'sports' && ['Game', 'Scrimmage', 'Tournament'].includes(formData.event_subtype) && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Home / Away *
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, game_type: 'home' })}
                        className={`flex-1 px-4 py-2 rounded-md font-medium transition-colors ${
                          formData.game_type === 'home'
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        🏠 Home
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, game_type: 'away' })}
                        className={`flex-1 px-4 py-2 rounded-md font-medium transition-colors ${
                          formData.game_type === 'away'
                            ? 'bg-orange-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        🚌 Away
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Arrive Early (minutes)
                    </label>
                    <select
                      value={String(formData.arrival_minutes)}
                      onChange={e => setFormData({ ...formData, arrival_minutes: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="15">15 min early</option>
                      <option value="30">30 min early</option>
                      <option value="45">45 min early</option>
                      <option value="60">1 hour early</option>
                      <option value="90">1.5 hours early</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Jersey Color (Sports games only) */}
              {formData.event_type === 'sports' && ['Game', 'Scrimmage', 'Tournament'].includes(formData.event_subtype) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Jersey Color (optional)
                  </label>
                  <input
                    type="text"
                    value={formData.jersey_color}
                    onChange={e => setFormData({ ...formData, jersey_color: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., White, Home Blue, Away Red"
                  />
                </div>
              )}
            </div>
          )}

          {/* URL (School/District events) */}
          {(scope === 'school' || scope === 'district') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <LinkIcon className="w-4 h-4 inline mr-1" />
                Event URL (optional)
              </label>
              <input
                type="url"
                value={formData.url}
                onChange={e => setFormData({ ...formData, url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://example.com/event-details"
              />
            </div>
          )}

          {/* Options */}
          <div className="space-y-4">
            {(scope === 'school' || scope === 'district') && (
              <>
                {/* Cancelled */}
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_cancelled}
                    onChange={e => setFormData({ ...formData, is_cancelled: e.target.checked })}
                    className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                  />
                  <span className="text-sm font-medium text-red-600">Cancelled</span>
                </label>

                {/* Day Type - Mutually Exclusive */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Day Type
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.no_school}
                        onChange={e => setFormData({
                          ...formData,
                          no_school: e.target.checked,
                          early_release: false,
                          remote_learning: false
                        })}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">No School Day</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.early_release}
                        onChange={e => setFormData({
                          ...formData,
                          early_release: e.target.checked,
                          no_school: false,
                          remote_learning: false
                        })}
                        className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                      />
                      <span className="text-sm text-gray-700">Early Release</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.remote_learning}
                        onChange={e => setFormData({
                          ...formData,
                          remote_learning: e.target.checked,
                          no_school: false,
                          early_release: false
                        })}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">Remote/Digital Learning Day</span>
                    </label>
                  </div>
                </div>

                {/* Transportation */}
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.affects_transportation}
                    onChange={e => setFormData({ ...formData, affects_transportation: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Affects Transportation</span>
                </label>
              </>
            )}
            {scope === 'family' && (
              <>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.affects_chores}
                    onChange={e => setFormData({ ...formData, affects_chores: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Affects Chores</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.needs_ride}
                    onChange={e => setFormData({ ...formData, needs_ride: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Needs Ride</span>
                </label>
              </>
            )}
          </div>

          {/* Attendees (Family events only) */}
          {scope === 'family' && availableAttendees.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Users className="w-4 h-4 inline mr-1" />
                {isKid ? 'This event is for you' : 'Attendees'}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {availableAttendees.map(user => (
                  <label key={user.id} className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={formData.attendees.includes(user.id)}
                      onChange={() => handleAttendeeToggle(user.id)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{user.display_name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
            </>
          )}

          {/* Overlap Warning */}
          {overlappingEvents.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-yellow-900 mb-2">
                    Event Overlap Detected
                  </h4>
                  <p className="text-sm text-yellow-800 mb-2">
                    This event overlaps with {overlappingEvents.length} existing event{overlappingEvents.length > 1 ? 's' : ''}:
                  </p>
                  <ul className="text-sm text-yellow-800 space-y-1">
                    {overlappingEvents.map((evt, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="font-medium">•</span>
                        <span>
                          {evt.title}
                          {evt.type === 'custody' && ' (Custody)'}
                          {!evt.all_day && evt.start_time && evt.end_time &&
                            ` (${new Date('2000-01-01T' + evt.start_time.substring(0, 5)).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} – ${new Date('2000-01-01T' + evt.end_time.substring(0, 5)).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })})`
                          }
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-yellow-700 mt-2">
                    You can still create this event, but be aware of the scheduling conflict.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-6 py-2 ${scopeColor[scope]} text-white rounded-md hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading
                ? (isEditing ? 'Saving...' : 'Creating...')
                : isEditing
                  ? 'Save Changes'
                  : scope === 'override'
                    ? 'Create Override'
                    : 'Create Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
